/* eslint-disable prefer-rest-params */
export default class SwipeNavigationInfo {
  public static lastTouchTime: number = Number.NaN; // LastTouchTime keeps track of when the last touch was.
  // If there's a browser navigation action within 500ms of a touch, we assume that's a swipe back or forward.

  public static lastDisableTime: number = Number.NaN;

  public static isTouching = false; // We use isTouching because iOS stops recording touch when you swipe to go forward.
  // The touchEnd isn't logged until later so we can use isTouching to recognise swipe forward events.

  public static lastSwipeNavigation: number = Number.NaN;

  public static lastPop = Number.NaN;

  public static lastPopWas = "forward" as "forward" | "back" | "neither";

  public static stateSequenceIndex = 0;

  private static swipeDelay = 500;

  public static temporarilyDisable(): void {
    this.lastDisableTime = new Date().getTime();
  }

  public static scrollX = 0;

  public static scrollY = 0;

  public static wasPop(): boolean {
    const now = new Date().getTime();
    return this.lastPop > now - 100;
  }

  public static wasSwipeNavigation(): boolean {
    const now = new Date().getTime();
    if (!this.isInit) {
      throw new Error("Must call SwipeNavigationInfo.init()");
    }
    return this.lastSwipeNavigation > now - 100;
  }

  private static isInit = false;

  public static init(): void {
    if (!this.isInit && this.isIOs()) {
      const self = SwipeNavigationInfo;
      self.stateSequenceIndex = window.history.state?.stateSequenceIndex || 0;

      (function (history) {
        const { pushState } = history;
        history.pushState = function (data, title, url?) {
          if (data) {
            self.stateSequenceIndex = self.stateSequenceIndex || 0;
            self.stateSequenceIndex += 1;
            data.stateSequenceIndex = SwipeNavigationInfo.stateSequenceIndex;
          }
          return pushState.apply(history, arguments as any);
        };

        const { go } = history;

        history.go = function () {
          self.temporarilyDisable();
          return go.apply(history, arguments as any);
        };

        const { back } = history;

        history.back = function () {
          self.temporarilyDisable();
          return back.apply(history, arguments as any);
        };

        const { forward } = history;

        history.forward = function () {
          self.temporarilyDisable();
          return forward.apply(history, arguments as any);
        };
      })(window.history);

      window.addEventListener("touchstart", () => {
        self.isTouching = true;
        self.lastTouchTime = new Date().getTime();
      });

      window.addEventListener("touchmove", () => {
        self.lastTouchTime = new Date().getTime();
      });

      window.addEventListener("touchend", () => {
        self.isTouching = false;
        self.lastTouchTime = new Date().getTime();
      });

      window.addEventListener("popstate", (event) => {
        const now = new Date().getTime();
        self.lastPop = now;
        const e = event || window.event;

        if (!e.state || self.stateSequenceIndex > (e.state?.stateSequenceIndex || 0)) {
          self.lastPopWas = "back";
        } else if (self.stateSequenceIndex < (e.state?.stateSequenceIndex || 0)) {
          self.lastPopWas = "forward";
        } else {
          self.lastPopWas = "neither";
        }

        self.scrollX = window.pageXOffset ?? window.scrollX;
        self.scrollY = window.pageYOffset ?? window.scrollY;

        self.stateSequenceIndex = e.state?.stateSequenceIndex;

        const wasTouching = self.lastTouchTime > now - self.swipeDelay;
        const isDisabled = self.lastDisableTime > now - self.swipeDelay;

        // wasTouching covers swipe to go back.
        // isTouching covers swipe to go forward.
        // Though they may overlap.
        if ((wasTouching || self.isTouching) && !isDisabled) {
          self.lastSwipeNavigation = now;

          // touchend isn't sent until the user touches the page event, so we need to make sure that
          // isTouching doesn't last indefinitely.
          self.isTouching = false;

          // During a swipe navigation, it's possible to input another swipe on the other side of the screen
          // - one that won't be registerered by the touch handler.
          // To work around this we reset the touch countdown.
          self.lastTouchTime = now;
        }
      });

      this.isInit = true;
    }
  }

  public static isIOs(): boolean {
    return true || /iPad|iPhone|iPod/.test(navigator.userAgent);
  }
}
